/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Collection } from 'ol';
import type { Feature, Map } from 'ol';
import { closestOnSegment, equals } from 'ol/coordinate';
import type { Coordinate } from 'ol/coordinate';
import { never, noModifierKeys } from 'ol/events/condition';
import { boundingExtent, buffer, containsCoordinate } from 'ol/extent';
import type { Extent } from 'ol/extent';
import type {
  Geometry,
  LineString,
  MultiLineString,
  MultiPolygon,
  Polygon,
} from 'ol/geom';
import Modify from 'ol/interaction/Modify';
import type { ModifyEvent } from 'ol/interaction/Modify';
import { toLonLat } from 'ol/proj';
import type { ProjectionLike } from 'ol/proj';
import Style from 'ol/style/Style';
import { useEffect, useRef } from 'react';
import { isDefaultMovable } from './moveFeatures';
import { COORDINATE_DECIMALS } from '../draw/mapDrawUtils';
import { FEATURE_VERTICE_HANDLE_IMAGE } from '../OlStyles';

const VERTEX_EDITABLE_GEOMETRY_TYPES: ReadonlySet<string> = new Set([
  'Polygon',
  'MultiPolygon',
  'LineString',
  'MultiLineString',
]);

export const CONSTRAINED_SELECTION_TYPES: ReadonlySet<string> = new Set([
  'box',
  'square',
  'rotatable-box',
  'circle',
  'oval',
  'freehand-line',
]);

export const hasEditableVertices = (feature: Feature): boolean => {
  const geometryType = feature.getGeometry()?.getType();
  if (!geometryType || !VERTEX_EDITABLE_GEOMETRY_TYPES.has(geometryType)) {
    return false;
  }
  const selectionType = feature.get('selectionType') as unknown;
  return (
    typeof selectionType !== 'string' ||
    !CONSTRAINED_SELECTION_TYPES.has(selectionType)
  );
};

export const isDefaultModifiable = (feature: Feature): boolean =>
  isDefaultMovable(feature) && hasEditableVertices(feature);

// One step for independent rounding of the same point, one step of margin for the projection round trip.
const SHARED_VERTEX_TOLERANCE_DEGREES = 2 * 10 ** -COORDINATE_DECIMALS;

const isSameLonLat = (first: Coordinate, second: Coordinate): boolean =>
  Math.abs(first[0] - second[0]) <= SHARED_VERTEX_TOLERANCE_DEGREES &&
  Math.abs(first[1] - second[1]) <= SHARED_VERTEX_TOLERANCE_DEGREES;

const isWithinSegmentBox = (
  point: Coordinate,
  start: Coordinate,
  end: Coordinate,
): boolean =>
  point[0] >= Math.min(start[0], end[0]) - SHARED_VERTEX_TOLERANCE_DEGREES &&
  point[0] <= Math.max(start[0], end[0]) + SHARED_VERTEX_TOLERANCE_DEGREES &&
  point[1] >= Math.min(start[1], end[1]) - SHARED_VERTEX_TOLERANCE_DEGREES &&
  point[1] <= Math.max(start[1], end[1]) + SHARED_VERTEX_TOLERANCE_DEGREES;

type Ring = Coordinate[];

interface RingData {
  coords: Ring;
  lonLat: Ring;
  extent: Extent;
}

interface RingSet {
  rings: RingData[];
  isClosed: boolean;
  write: () => void;
}

const readRingSet = (
  geometry: Geometry | undefined,
  projection: ProjectionLike,
): RingSet | undefined => {
  const toRingData = (coords: Ring): RingData => {
    const lonLat = coords.map((coordinate) => toLonLat(coordinate, projection));
    return {
      coords,
      lonLat,
      extent: buffer(boundingExtent(lonLat), SHARED_VERTEX_TOLERANCE_DEGREES),
    };
  };

  switch (geometry?.getType()) {
    case 'Polygon': {
      const polygon = geometry as Polygon;
      const coordinates = polygon.getCoordinates();
      return {
        rings: coordinates.map(toRingData),
        isClosed: true,
        write: (): void => polygon.setCoordinates(coordinates),
      };
    }
    case 'MultiPolygon': {
      const multiPolygon = geometry as MultiPolygon;
      const coordinates = multiPolygon.getCoordinates();
      return {
        rings: coordinates.flat(1).map(toRingData),
        isClosed: true,
        write: (): void => multiPolygon.setCoordinates(coordinates),
      };
    }
    case 'LineString': {
      const lineString = geometry as LineString;
      const coordinates = lineString.getCoordinates();
      return {
        rings: [toRingData(coordinates)],
        isClosed: false,
        write: (): void => lineString.setCoordinates(coordinates),
      };
    }
    case 'MultiLineString': {
      const multiLineString = geometry as MultiLineString;
      const coordinates = multiLineString.getCoordinates();
      return {
        rings: coordinates.map(toRingData),
        isClosed: false,
        write: (): void => multiLineString.setCoordinates(coordinates),
      };
    }
    default:
      return undefined;
  }
};

const connectVertexToRing = (
  vertex: Coordinate,
  vertexLonLat: Coordinate,
  ring: RingData,
  isClosed: boolean,
  projection: ProjectionLike,
): boolean => {
  if (!containsCoordinate(ring.extent, vertexLonLat)) {
    return false;
  }

  const distinctCount = isClosed ? ring.coords.length - 1 : ring.coords.length;

  for (let i = 0; i < distinctCount; i += 1) {
    if (equals(ring.coords[i], vertex)) {
      return false;
    }
  }

  for (let i = 0; i < distinctCount; i += 1) {
    if (isSameLonLat(ring.lonLat[i], vertexLonLat)) {
      ring.coords.splice(i, 1, [...vertex]);
      ring.lonLat.splice(i, 1, [...vertexLonLat]);
      if (isClosed && i === 0) {
        ring.coords.splice(ring.coords.length - 1, 1, [...vertex]);
        ring.lonLat.splice(ring.lonLat.length - 1, 1, [...vertexLonLat]);
      }
      return true;
    }
  }

  for (let i = 0; i < ring.coords.length - 1; i += 1) {
    if (
      isWithinSegmentBox(vertexLonLat, ring.lonLat[i], ring.lonLat[i + 1]) &&
      isSameLonLat(
        toLonLat(
          closestOnSegment(vertex, [ring.coords[i], ring.coords[i + 1]]),
          projection,
        ),
        vertexLonLat,
      )
    ) {
      ring.coords.splice(i + 1, 0, [...vertex]);
      ring.lonLat.splice(i + 1, 0, [...vertexLonLat]);
      return true;
    }
  }

  return false;
};

export const connectSharedVertices = (
  features: Feature[],
  projection: ProjectionLike,
): void => {
  const ringSets = features
    .map((feature) => readRingSet(feature.getGeometry(), projection))
    .filter((ringSet): ringSet is RingSet => Boolean(ringSet));

  const changed = new Set<RingSet>();

  ringSets.forEach((source) => {
    source.rings.forEach((sourceRing) => {
      const vertexCount = source.isClosed
        ? sourceRing.coords.length - 1
        : sourceRing.coords.length;
      for (let index = 0; index < vertexCount; index += 1) {
        const vertex = sourceRing.coords[index];
        const vertexLonLat = sourceRing.lonLat[index];
        ringSets.forEach((target) => {
          if (target === source) {
            return;
          }
          target.rings.forEach((ring) => {
            if (
              connectVertexToRing(
                vertex,
                vertexLonLat,
                ring,
                target.isClosed,
                projection,
              )
            ) {
              changed.add(target);
            }
          });
        });
      }
    });
  });

  changed.forEach((ringSet) => ringSet.write());
};

export const modifyHandleStyle = new Style({
  image: FEATURE_VERTICE_HANDLE_IMAGE,
});

export interface CreateModifyFeaturesOptions {
  features: Feature[];
  isModifiable: (feature: Feature) => boolean;
  projection: ProjectionLike;
  onModifyEnd: (features: Feature[]) => void;
  onModifyStart?: () => void;
}

export interface ModifyFeaturesInteraction {
  modify: Modify;
  dispose: () => void;
}

export const createModifyFeatures = ({
  features,
  isModifiable,
  projection,
  onModifyEnd,
  onModifyStart,
}: CreateModifyFeaturesOptions): ModifyFeaturesInteraction | undefined => {
  const modifiableFeatures = features.filter(isModifiable);

  if (modifiableFeatures.length === 0) {
    return undefined;
  }

  connectSharedVertices(modifiableFeatures, projection);

  const collection = new Collection(modifiableFeatures);

  const modify = new Modify({
    features: collection,
    condition: noModifierKeys,
    insertVertexCondition: never,
    deleteCondition: never,
    style: modifyHandleStyle,
  });

  modify.on('modifystart', () => {
    onModifyStart?.();
  });

  modify.on('modifyend', (event: ModifyEvent) => {
    onModifyEnd(event.features.getArray() as Feature[]);
  });

  return { modify, dispose: (): void => collection.clear() };
};

export interface UseModifyFeaturesOptions {
  map?: Map | null;
  features: Feature[];
  active: boolean;
  isModifiable?: (feature: Feature) => boolean;
  onModifyEnd?: (features: Feature[]) => void;
  onModifyStart?: () => void;
}

export const useModifyFeatures = ({
  map,
  features,
  active,
  isModifiable,
  onModifyEnd,
  onModifyStart,
}: UseModifyFeaturesOptions): void => {
  const isModifiableRef = useRef(isModifiable);
  const onModifyEndRef = useRef(onModifyEnd);
  const onModifyStartRef = useRef(onModifyStart);
  useEffect(() => {
    isModifiableRef.current = isModifiable;
    onModifyEndRef.current = onModifyEnd;
    onModifyStartRef.current = onModifyStart;
  });

  useEffect((): (() => void) => {
    if (!map || !active || !features.length) {
      return (): void => {};
    }

    const modifyFeatures = createModifyFeatures({
      features,
      isModifiable: (feature) =>
        (isModifiableRef.current ?? isDefaultModifiable)(feature),
      projection: map.getView().getProjection(),
      onModifyEnd: (modifiedFeatures) =>
        onModifyEndRef.current?.(modifiedFeatures),
      onModifyStart: () => onModifyStartRef.current?.(),
    });

    if (!modifyFeatures) {
      return (): void => {};
    }

    map.addInteraction(modifyFeatures.modify);

    return (): void => {
      modifyFeatures.dispose();
      map.removeInteraction(modifyFeatures.modify);
    };
  }, [map, features, active]);
};
